import React from 'react';

import { Pagination, Progress, Select, Typography } from 'antd';

import ReportSection from '../ReportSection';
import type { SectionStatus } from '../SectionStatus';
import type { SortDirection } from '../types';
import styles from './styles.module.less';

const { Text } = Typography;

export interface RankingItem {
  id: string;
  name: string;
  /** Bar fill, 0–100. */
  percent: number;
  color: string;
  value: React.ReactNode;
  detail: string;
}

export interface RankingSortConfig {
  value: SortDirection;
  options: Array<{ value: SortDirection; label: string }>;
  ariaLabel: string;
  onChange: (value: SortDirection) => void;
}

export interface RankingPagination {
  /** Zero-based page. */
  page: number;
  pageSize: number;
  total: number;
  ariaLabel: string;
  onChange: (page: number) => void;
}

export interface RankingPanelProps {
  title: string;
  description: string;
  /** Items of the current page. */
  items: RankingItem[];
  sort: RankingSortConfig;
  pagination: RankingPagination;
  /** Footer text, e.g. "Showing 1–5 of 24". */
  rangeLabel: string;
  /** Load state of the ranking; the heading stays visible in every state. */
  status?: SectionStatus;
}

/** Sortable, paginated ranking with a proportional bar per row. */
const RankingPanel: React.FC<RankingPanelProps> = ({
  title,
  description,
  items,
  sort,
  pagination,
  rangeLabel,
  status,
}) => {
  const rankOffset = pagination.page * pagination.pageSize;

  return (
    <ReportSection
      title={title}
      description={description}
      status={status}
      headerExtra={(
        <Select
          size="small"
          aria-label={sort.ariaLabel}
          value={sort.value}
          options={sort.options}
          onChange={sort.onChange}
        />
      )}
    >
      <div className={styles.list}>
        {items.map((item, index) => (
          <div className={styles.row} key={item.id}>
            <Text type="secondary" className={styles.rank}>
              #{rankOffset + index + 1}
            </Text>
            <Text className={styles.name} ellipsis={{ tooltip: item.name }}>
              {item.name}
            </Text>
            <Progress percent={item.percent} showInfo={false} strokeColor={item.color} />
            <div className={styles.value}>
              <Text strong>{item.value}</Text>
              <Text type="secondary">{item.detail}</Text>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <Text type="secondary">{rangeLabel}</Text>
        {pagination.total > pagination.pageSize && (
          <Pagination
            aria-label={pagination.ariaLabel}
            current={pagination.page + 1}
            pageSize={pagination.pageSize}
            simple
            showSizeChanger={false}
            total={pagination.total}
            onChange={nextPage => pagination.onChange(nextPage - 1)}
          />
        )}
      </div>
    </ReportSection>
  );
};

export default RankingPanel;
